import { useEffect, useMemo } from 'react';
import { filter, isEqual, keyBy } from 'lodash';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { CONVERSION_STATUS, CONVERSION_TYPE } from 'modules/tracking/config';
import { useFetchedConversionsContext } from '../../../contexts/FetchedConversionsContext';

export const useConversions = () => {
    const {
        conversionsResult: originalConversions,
        conversionsFetchSuccess,
        unfilteredFetchedConversions,
    } = useFetchedConversionsContext();

    const { value: persistentConversions = [], onChange: setPersistentConversions } = useFormFieldValue({
        field: 'conversions',
    });

    const { value: existingConversionIdsOnThisPage } = useFormFieldValue({
        field: 'existingConversionIdsOnThisPage',
    });

    useEffect(() => {
        if (
            conversionsFetchSuccess &&
            originalConversions &&
            persistentConversions &&
            persistentConversions.length === 0
        ) {
            setPersistentConversions(originalConversions);
        }
    }, [conversionsFetchSuccess, originalConversions, persistentConversions, setPersistentConversions]);

    const activeConversions = useMemo(
        () => filter(persistentConversions, conv => conv.status !== CONVERSION_STATUS.ARCHIVED),
        [persistentConversions]
    );

    const existingConversions = useMemo(
        () => filter(activeConversions, activeConv => existingConversionIdsOnThisPage.includes(activeConv.id)),
        [activeConversions, existingConversionIdsOnThisPage]
    );

    const conversionsToSave = useMemo(() => {
        const originalMap = keyBy(originalConversions, 'id');
        return filter(persistentConversions, current => {
            const original = originalMap[current.id];
            if (!original) {
                return true;
            }
            if (current.status === CONVERSION_STATUS.ARCHIVED && original.status !== CONVERSION_STATUS.ARCHIVED) {
                return true;
            }
            if (current.status !== CONVERSION_STATUS.ARCHIVED && !isEqual(current, original)) {
                return true;
            }
            return false;
        });
    }, [persistentConversions, originalConversions]);

    const displayedConversions = useMemo(() => {
        const originalMap = keyBy(originalConversions, 'id');
        return filter(
            persistentConversions,
            c =>
                c.status !== CONVERSION_STATUS.ARCHIVED ||
                (originalMap[c.id] && originalMap[c.id].status !== CONVERSION_STATUS.ARCHIVED)
        );
    }, [persistentConversions, originalConversions]);

    const allPurposeEditableEvents = useMemo(() => {
        const isUrlType = event => event.type === CONVERSION_TYPE.BASIC;
        const editableSet = new Set();

        (displayedConversions || []).forEach(event => {
            if (
                (existingConversionIdsOnThisPage.includes(event.id) || isUrlType(event)) &&
                event.status !== CONVERSION_STATUS.ARCHIVED
            ) {
                editableSet.add(event);
            }
        });
        return Array.from(editableSet);
    }, [displayedConversions, existingConversionIdsOnThisPage]);

    return {
        persistentConversions,
        setPersistentConversions,
        activeConversions,
        existingConversions,
        conversionsToSave,
        displayedConversions,
        allPurposeEditableEvents,
        originalConversions,
        unfilteredFetchedConversions,
    };
};
